import state from "./state";
import pagination from "./pagination";
import api from "./api";
import urlParams from "./urlParams";

/**
 * Universal Filter Manager (Alpine.js Component)
 *
 * Provides hybrid filtering for multiple content types: cases, news, and publishings.
 * Implements real-time filtering with server-side pagination, imaage transformations and URL parameters.
 * Handles different field structures per content type dynamically.
 *
 * @param {Object} config - Configuration object
 */
export function filterManager(config) {
	const {
		contentType,
		filterOptions,
		initialData,
		totalCount,
		perPageDesktop = 12,
		perPageMobile = 6,
		imageSizes = null,
		siteHandle = null,
	} = config;

	// Convert Craft CMS pairs to array format for Alpine.js templates
	const convertedFilterOptions = {};
	for (const [key, pairs] of Object.entries(filterOptions)) {
		convertedFilterOptions[key] = Object.entries(pairs).map(
			([id, title]) => ({
				id: parseInt(id),
				title,
			}),
		);
	}

	return {
		contentType,
		filterOptions: convertedFilterOptions,

		// Combined state from mixins
		...state(),
		...pagination(1, perPageDesktop, perPageMobile),
		...api(contentType, imageSizes, siteHandle),
		...urlParams(),

		// Display State
		initialEntries: initialData,
		filteredCases: [],
		displayedCases: [],
		totalCases: totalCount,
		originalTotalCount: totalCount,
		filterHeight: 0,
		activeClass: "filter-active",
		inactiveClass: "filter-inactive",
		appliedFilterCount: 0,

		get activeFilterCount() {
			let count = 0;
			count += this.selectedCategories.length;
			count += this.selectedTopics.length;
			count += this.selectedIndustries.length;
			if (this.searchTerm && this.searchTerm.trim() !== "") count += 1;
			return count;
		},

		updateAppliedFilterCount() {
			this.appliedFilterCount = this.activeFilterCount;
		},

		init() {
			// Load state from URL parameters if present
			this.loadFromUrlParams();
			this.updateAppliedFilterCount();

			// Set perPage based on current viewport before initial pagination
			this.updatePerPage();

			// Start with initial entries data (first page)
			this.filteredCases = [...this.initialEntries];
			this.updatePagination();

			// Apply filters if URL params were loaded
			if (
				this.selectedCategories.length > 0 ||
				this.selectedTopics.length > 0 ||
				this.selectedIndustries.length > 0 ||
				this.searchTerm !== "" ||
				this.currentPage > 1
			) {
				this.applyFilters(false);
			}

			// Listen for browser back/forward navigation
			window.addEventListener("popstate", () => {
				this.loadFromUrlParams();
				this.applyFilters(false);
			});

			// Re-evaluate perPage on viewport resize
			this._resizeHandler = () => {
				if (this.updatePerPage()) {
					// If no filters are active, recalc locally; otherwise refetch
					if (this.activeFilterCount === 0) {
						this.updatePagination();
					} else {
						this.applyFilters();
					}
				}
			};
			window.addEventListener("resize", this._resizeHandler);
		},

		// Actions combining logic from multiple mixins

		toggleFilterInstant(arrayName, id) {
			this.toggleFilter(arrayName, id);
			this.startFilter();
		},

		handleSearch() {
			this.currentPage = 1;
			this.applyFilters(false);
			this.updateUrlParams();
		},

		startFilter() {
			this.currentPage = 1;
			this.applyFilters(false);
			this.updateUrlParams();
			this.updateAppliedFilterCount();
		},

		resetFilters() {
			this.resetState();
			this.currentPage = 1;

			this.filteredCases = [...this.initialEntries];
			this.displayedCases = [...this.initialEntries];
			this.totalCases = this.originalTotalCount;
			this.updatePerPage();
			this.updatePagination();
			this.updateUrlParams();
			this.updateAppliedFilterCount();
		},

		async applyFilters(shouldScroll = false) {
			const scrollY = window.scrollY;
			const offset = (this.currentPage - 1) * this.perPage;

			const data = await this.fetchEntries({
				selectedCategories: this.selectedCategories,
				selectedTopics: this.selectedTopics,
				selectedIndustries: this.selectedIndustries,
				searchTerm: this.searchTerm,
				perPage: this.perPage,
				offset: offset,
			});

			if (data) {
				this.filteredCases = data.entries;
				this.displayedCases = data.entries;
				this.totalCases = data.totalCount;
				this.totalPages = Math.ceil(data.totalCount / this.perPage);

				if (!shouldScroll) {
					requestAnimationFrame(() => {
						window.scrollTo(0, scrollY);
					});
				}
			}
		},

		updatePagination() {
			// For initial load, calculate based on initial entries
			if (
				this.filteredCases.length === this.initialEntries.length &&
				this.activeFilterCount === 0
			) {
				this.totalPages = Math.ceil(this.totalCases / this.perPage);
			} else {
				this.totalPages = Math.ceil(
					this.filteredCases.length / this.perPage,
				);
			}
			this.currentPage = Math.min(this.currentPage, this.totalPages || 1);

			// Update displayed cases logic (if needed locally, though usually API handles this)
			if (
				this.filteredCases.length > 0 &&
				this.displayedCases.length === 0
			) {
				const start = (this.currentPage - 1) * this.perPage;
				const end = start + this.perPage;
				this.displayedCases = this.filteredCases.slice(start, end);
			}
		},

		async goToPage(page, scrollToElement = null) {
			this.currentPage = page;
			this.updatePerPage();
			await this.applyFilters(false);
			this.updateUrlParams();

			if (scrollToElement) {
				const element = document.getElementById(scrollToElement);
				if (element) {
					setTimeout(() => {
						window.scrollTo({
							top: element.offsetTop - 240,
							behavior: "smooth",
						});
					}, 50);
				}
			}
		},
	};
}
